import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

/**
 * Premium Button primitive.
 *
 * Visual upgrade highlights:
 *  - rounded-xl by default for a softer, modern silhouette
 *  - transition-all for smooth color + shadow interactions
 *  - light hover lift via shadow-glow-blue on the primary action
 *  - outline / ghost / secondary variants pick up the translucent border
 *    palette (border-white/10) so they sit naturally on dark surfaces
 *  - active:scale-[0.98] for a tactile feel on mobile (outdoor tap targets)
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-semibold tracking-tightish transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98] [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90 hover:shadow-glow-blue",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-white/10 bg-white/0 text-foreground shadow-sm hover:bg-white/5 hover:border-white/20",
        secondary:
          "bg-secondary text-secondary-foreground border border-white/5 shadow-sm hover:bg-secondary/80 hover:border-white/10",
        ghost:
          "hover:bg-white/5 hover:text-foreground",
        link:
          "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-8 rounded-lg px-3 text-xs",
        lg: "h-12 rounded-2xl px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props}
    />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
